 
import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Upload, CheckCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TeacherSalariesPage = () => {
  const [salaries, setSalaries] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [selectedTerm, setSelectedTerm] = useState('');
  const [selectedMonth, setSelectedMonth] = useState('all');
  const [summary, setSummary] = useState(null);
  const [formData, setFormData] = useState({
    teacherId: '',
    termId: '',
    academicYear: '',
    monthNumber: '',
    grossSalary: '',
    deductions: '',
    paymentDate: '',
    paymentMethod: ''
  });

  const fetchTeachers = useCallback(async () => {
    try {
      const response = await fetch(`${API}/teachers`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      if (!response.ok) throw new Error('Failed to fetch teachers');
      const data = await response.json();
      setTeachers(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching teachers:', error);
      setTeachers([]);
    }
  }, []);

  const fetchSalaries = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (selectedTerm) params.append('termId', selectedTerm);
      if (selectedMonth && selectedMonth !== 'all') params.append('monthNumber', selectedMonth);

      const response = await fetch(`${API}/teacher-salaries?${params}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      const data = await response.json();
      setSalaries(Array.isArray(data) ? data : (Array.isArray(data?.salaries) ? data.salaries : []));
    } catch (error) {
      console.error('Error fetching salaries:', error);
      setSalaries([]);
    } finally {
      setLoading(false);
    }
  }, [selectedTerm, selectedMonth]);

  const fetchSalaryByMonth = useCallback(async () => {
    try {
      const year = formData.academicYear || new Date().getFullYear();
      const response = await fetch(`${API}/teacher-salaries/summary/${selectedTerm}?academicYear=${year}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      const data = await response.json();
      setSummary(data || null);
    } catch (error) {
      console.error('Error fetching summary:', error);
      setSummary(null);
    }
  }, [selectedTerm, formData.academicYear]);

  useEffect(() => {
    fetchTeachers();
  }, [fetchTeachers]);

  useEffect(() => {
    fetchSalaries();
  }, [fetchSalaries]);

  useEffect(() => {
    if (selectedTerm && selectedMonth) {
      fetchSalaryByMonth();
    }
  }, [selectedTerm, selectedMonth, fetchSalaryByMonth]);

  const handleAddSalary = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${API}/teacher-salaries`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        setShowAddForm(false);
        setFormData({
          teacherId: '',
          termId: '',
          academicYear: '',
          monthNumber: '',
          grossSalary: '',
          deductions: '',
          paymentDate: '',
          paymentMethod: ''
        });
        fetchSalaries();
      }
    } catch (error) {
      console.error('Error adding salary:', error);
    }
  };

  const handleMarkPaid = async (salaryId) => {
    try {
      const response = await fetch(`${API}/teacher-salaries/${salaryId}/mark-paid`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ paymentDate: new Date().toISOString().split('T')[0] })
      });

      if (response.ok) {
        fetchSalaries();
      }
    } catch (error) {
      console.error('Error marking paid:', error);
    }
  };

  const handleUploadProof = async (salaryId) => {
    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.accept = '.pdf,.png,.jpg,.jpeg';
    fileInput.onchange = async (e) => {
      const file = e.target.files[0];
      if (file) {
        const formDataWithFile = new FormData();
        formDataWithFile.append('file', file);
        
        try {
          const response = await fetch(`${API}/teacher-salaries/${salaryId}/upload-proof`, {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`
            },
            body: formDataWithFile
          });

          if (response.ok) {
            fetchSalaries();
          }
        } catch (error) {
          console.error('Error uploading proof:', error);
        }
      }
    };
    fileInput.click();
  };

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Teacher Salaries</h1>
            <p className="text-gray-600 mt-2">Record and track teacher monthly payments</p>
          </div>
          <Button onClick={() => setShowAddForm(!showAddForm)} className="gap-2">
            <Plus size={20} /> Record Salary
          </Button>
        </div>

        {/* Monthly Summary Cards */}
        {summary && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            {[1, 2, 3].map(month => (
              <Card key={month}>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium">Month {month}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <div>
                      <p className="text-xs text-gray-600">Gross Salary</p>
                      <p className="text-xl font-bold">
                        ${summary[`month${month}`]?.totalGross?.toFixed(2) || '0.00'}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-600">Net Paid</p>
                      <p className="text-lg font-semibold">
                        ${summary[`month${month}`]?.totalNet?.toFixed(2) || '0.00'}
                      </p>
                    </div>
                    <p className="text-xs text-gray-500">
                      {summary[`month${month}`]?.teachersCount || 0} teachers
                    </p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Add Salary Form */}
        {showAddForm && (
          <Card className="mb-8">
            <CardHeader>
              <CardTitle>Record Teacher Salary Payment</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleAddSalary} className="grid grid-cols-2 gap-4">
                <Select value={formData.teacherId} onValueChange={(value) => setFormData({...formData, teacherId: value})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select Teacher" />
                  </SelectTrigger>
                  <SelectContent>
                    {teachers.map(teacher => (
                      <SelectItem key={teacher.id} value={teacher.id}>{teacher.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select value={formData.monthNumber} onValueChange={(value) => setFormData({...formData, monthNumber: value})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Month" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">Month 1</SelectItem>
                    <SelectItem value="2">Month 2</SelectItem>
                    <SelectItem value="3">Month 3</SelectItem>
                  </SelectContent>
                </Select>

                <Input
                  type="number"
                  placeholder="Gross Salary"
                  value={formData.grossSalary}
                  onChange={(e) => setFormData({...formData, grossSalary: e.target.value})}
                  required
                />

                <Input
                  type="number"
                  placeholder="Deductions"
                  value={formData.deductions}
                  onChange={(e) => setFormData({...formData, deductions: e.target.value})}
                  min="0"
                />

                <Input
                  type="date"
                  placeholder="Payment Date"
                  value={formData.paymentDate}
                  onChange={(e) => setFormData({...formData, paymentDate: e.target.value})}
                  required
                />

                <Input
                  type="text"
                  placeholder="Payment Method"
                  value={formData.paymentMethod}
                  onChange={(e) => setFormData({...formData, paymentMethod: e.target.value})}
                />

                <Button type="submit" className="col-span-2">Record Salary</Button>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Filters */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
          <Select value={selectedMonth} onValueChange={setSelectedMonth}>
            <SelectTrigger>
              <SelectValue placeholder="Filter by Month" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Months</SelectItem>
              <SelectItem value="1">Month 1</SelectItem>
              <SelectItem value="2">Month 2</SelectItem>
              <SelectItem value="3">Month 3</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Salaries Table */}
        <Card>
          <CardHeader>
            <CardTitle>Salary Records</CardTitle>
            <CardDescription>{salaries.length} salary payments</CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <p>Loading salaries...</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="border-b">
                    <tr>
                      <th className="text-left py-2">Teacher</th>
                      <th className="text-left py-2">Month</th>
                      <th className="text-left py-2">Gross</th>
                      <th className="text-left py-2">Deductions</th>
                      <th className="text-left py-2">Net Paid</th>
                      <th className="text-left py-2">Payment Date</th>
                      <th className="text-left py-2">Status</th>
                      <th className="text-left py-2">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {salaries.map(salary => (
                      <tr key={salary.id} className="border-b hover:bg-gray-50">
                        <td className="py-3">{salary.teacher_name}</td>
                        <td className="py-3">Month {salary.month_number}</td>
                        <td className="py-3">${salary.gross_salary}</td>
                        <td className="py-3">${salary.deductions}</td>
                        <td className="py-3 font-semibold">${salary.net_salary_paid}</td>
                        <td className="py-3">{salary.payment_date || '-'}</td>
                        <td className="py-3">
                          <span className={`px-2 py-1 rounded-full text-xs ${
                            salary.status === 'Paid' ? 'bg-green-100 text-green-800' :
                            'bg-yellow-100 text-yellow-800'
                          }`}>
                            {salary.status}
                          </span>
                        </td>
                        <td className="py-3 flex gap-2">
                          <button
                            onClick={() => handleUploadProof(salary.id)}
                            className="text-blue-600 hover:text-blue-800"
                            title="Upload Proof"
                          >
                            <Upload size={18} />
                          </button>
                          {salary.status === 'Pending' && (
                            <button
                              onClick={() => handleMarkPaid(salary.id)}
                              className="text-green-600 hover:text-green-800"
                              title="Mark as Paid"
                            >
                              <CheckCircle size={18} />
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default TeacherSalariesPage;
